import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import type { WeeklyPlanPoint } from '@loggy/shared';

import { Text } from '../design/index.js';
import { ChartLegend } from './ChartLegend.js';
import type { ChartLegendItem } from './ChartLegend.js';
import { buildTicks, buildValueDomain, changeBarSpan, linearPath } from './chartMath.js';
import styles from './WeightPlanChart.module.css';
import {
  asWeightDelta,
  asWeightDeltaRange,
  planBarGeometry,
  planBarPath,
  requiredRatePolylines,
  symmetricAroundZero,
} from './weightPlanChartMath.js';
import type { PlanBarGeometry, RatePoint } from './weightPlanChartMath.js';

export { planBarGeometry, planBarPath, requiredRatePolylines } from './weightPlanChartMath.js';
export type { PlanBarGeometry, RatePoint } from './weightPlanChartMath.js';

/**
 * A pure, presentational, hand-written SVG chart. It takes data as props,
 * fetches nothing, and imports nothing from `web/src/api.ts` — the same
 * contract `MeasurementChart` holds. Nothing wires this into a screen yet;
 * that is a later step.
 */
export interface WeightPlanChartProps {
  points: WeeklyPlanPoint[];
  unit: string;
  precision: number;
  /**
   * The moderate-deficit band `healthyWeeklyRange` computes for this person.
   * Only present with a body profile — absent or `null` draws no band at
   * all. Never invented: a chart with no range to show says nothing rather
   * than guessing one.
   */
  healthyRange?: { min: number; max: number } | null;
  /**
   * The commonly cited, profile-free gradual range `gradualReferenceRange`
   * always has an answer for. Drawn as a dashed outline rather than a fill —
   * distinct from `healthyRange`'s solid band — so the two read as what they
   * are: a personal estimate and a looser population reference, which can
   * both be on screen at once. Absent or `null` draws nothing, the same rule
   * `healthyRange` follows.
   */
  gradualRange?: { min: number; max: number } | null;
  /** The reader's own recent pace, drawn as a dashed reference line. Absent
   *  or `null` draws nothing. */
  recentRateKgPerWeek?: number | null;
  /**
   * Which way the reader chose to go, from their own baseline and target.
   *
   * Used ONLY to turn "movement towards the target" into the weight change a
   * scale would have shown, so a kilo lost reads as −1.0 rather than +1.0.
   * Never to decide which direction is good — that is not this chart's call
   * and never was.
   */
  goingDown: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// The component
// ---------------------------------------------------------------------------
//
// The pure geometry (`planBarGeometry`, `planBarPath`, `requiredRatePolylines`)
// lives in `weightPlanChartMath.ts`, imported and re-exported above — the same
// split `chartMath.ts`/`MeasurementChart.tsx` uses, and for the same reason:
// it can be read, and tested with `node:test`, without the test runner also
// having to load this file's CSS module import.

const MARGIN = { top: 16, right: 16, bottom: 28, left: 46 };
const MIN_HEIGHT = 200;
/**
 * Simpler than `MeasurementChart`'s own `heightForWidth`: this chart is
 * always the small, single-purpose one the brief asks for, never the whole
 * screen's tallest element, so it holds one aspect ratio and a floor rather
 * than also chasing a share of the viewport's height.
 */
const TARGET_ASPECT = 3.6;
const DEFAULT_WIDTH = 640;
const TICK_COUNT = 4;
const BAR_MAX_WIDTH = 28;
const BAR_GAP = 3;
/** So a flat week reads as flat, not as missing. */
const MIN_BAR_HEIGHT = 1.5;
const BAR_RADIUS = 4;
const UNKNOWN_MARK_RADIUS = 3.5;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function heightForWidth(width: number): number {
  return Math.round(Math.max(MIN_HEIGHT, width / TARGET_ASPECT));
}

function weekTime(point: WeeklyPlanPoint): number {
  return new Date(point.weekStart).getTime();
}

function formatValue(value: number, precision: number): string {
  return value.toFixed(Math.max(0, precision));
}

const axisTickFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/** "-1.2 kg", "+0.3 kg" — the sign is the reading, so it is never dropped. */
function formatSigned(value: number, unit: string, precision: number): string {
  const magnitude = formatValue(Math.abs(value), precision);
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${magnitude} ${unit}`;
}

function formatActualText(change: number | null, unit: string, precision: number): string {
  if (change === null) return 'not enough readings that week to measure a change';
  if (change === 0) return 'no measurable movement that week';
  return formatSigned(change, unit, precision);
}

function formatRequiredText(requiredRateKg: number | null, unit: string, precision: number): string {
  if (requiredRateKg === null) return 'no required pace for that week';
  return `${formatSigned(requiredRateKg, unit, precision)} per week required`;
}

interface Plotted {
  point: WeeklyPlanPoint;
  x: number;
  left: number;
  width: number;
  bar: PlanBarGeometry | null;
}

/**
 * Weekly weight-loss targets against what actually happened: a bar per week
 * for `actualChangeKg`, a line for the required pace (which moves — easing
 * off when the reader is ahead, steepening when behind), an optional healthy
 * band, an optional gradual-range outline, an optional dashed recent-pace
 * line, and an always-drawn zero baseline.
 */
export function WeightPlanChart({
  points: pointsTowardsTarget,
  unit,
  precision,
  healthyRange: healthyRangeTowardsTarget,
  gradualRange: gradualRangeTowardsTarget,
  recentRateKgPerWeek: recentRateTowardsTarget,
  goingDown,
  className,
}: WeightPlanChartProps) {
  /**
   * Everything below this point is a real weight delta: down negative, up
   * positive. The props arrive as movement TOWARDS the target, which is what
   * lets the maths serve a loss goal and a gain goal with one set of rules —
   * and is the wrong thing to show, because "1.2" for a kilo lost reads as a
   * kilo gained. Flipped once, here, so no drawing or sentence below has to
   * remember which convention it is in.
   */
  const points = useMemo(
    () =>
      pointsTowardsTarget.map((p) => ({
        ...p,
        actualChangeKg: asWeightDelta(p.actualChangeKg, goingDown),
        requiredRateKg: asWeightDelta(p.requiredRateKg, goingDown),
      })),
    [pointsTowardsTarget, goingDown],
  );
  const healthyRange = asWeightDeltaRange(healthyRangeTowardsTarget, goingDown);
  const gradualRange = asWeightDeltaRange(gradualRangeTowardsTarget, goingDown);
  const recentRateKgPerWeek = asWeightDelta(recentRateTowardsTarget ?? null, goingDown);
  const frameRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const focusedRef = useRef(false);

  // Keeps the viewBox's width equal to the container's real CSS width, the
  // same reasoning `MeasurementChart` gives: a chart stretched via
  // `width: 100%` on a fixed viewBox would shrink its own embedded text at
  // narrow widths instead of keeping 1 user unit = 1 real pixel.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(1, Math.round(entry.contentRect.width)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const height = heightForWidth(width);
  const plot = useMemo(
    () => ({ x0: MARGIN.left, x1: width - MARGIN.right, y0: MARGIN.top, y1: height - MARGIN.bottom }),
    [width, height],
  );

  const sorted = useMemo(() => [...points].sort((a, b) => weekTime(a) - weekTime(b)), [points]);

  const { xScale, yScale, ticks, minT, maxT } = useMemo(() => {
    const times = sorted.map(weekTime);
    const minT = times.length > 0 ? Math.min(...times) : 0;
    const maxT = times.length > 0 ? Math.max(...times) : 1;
    const xScale = (t: number) =>
      minT === maxT ? (plot.x0 + plot.x1) / 2 : plot.x0 + ((t - minT) / (maxT - minT)) * (plot.x1 - plot.x0);

    // The value domain is forced through zero (`suggestedAxis: { min: 0, max:
    // 0 }`) — the same technique `MeasurementChart`'s own change strip uses —
    // so a plan that is entirely ahead, or entirely behind, still reads
    // against a real zero rather than an axis that starts at its own
    // least-extreme week.
    const values: number[] = [];
    for (const p of sorted) {
      if (p.actualChangeKg !== null) values.push(p.actualChangeKg);
      if (p.requiredRateKg !== null) values.push(p.requiredRateKg);
    }
    if (healthyRange) values.push(healthyRange.min, healthyRange.max);
    if (gradualRange) values.push(gradualRange.min, gradualRange.max);
    if (recentRateKgPerWeek !== undefined && recentRateKgPerWeek !== null) values.push(recentRateKgPerWeek);

    // Symmetric, so the zero line lands in the MIDDLE of the plot rather than
    // wherever the data happens to put it. Weight can go up as well as down,
    // and an axis fitted to a run of good weeks pushes zero to the top and
    // leaves a bad week nowhere to be drawn.
    const domain = symmetricAroundZero(
      buildValueDomain({ values, precision, suggestedAxis: { min: 0, max: 0 } }),
    );
    const tickValues = buildTicks(domain, TICK_COUNT);
    const firstTick = tickValues[0] ?? domain.min;
    const lastTick = tickValues[tickValues.length - 1] ?? domain.max;
    // Re-centred after the ticks widen it, or a rounded tick on one side
    // alone would nudge zero back off the middle.
    const finalDomain = symmetricAroundZero({
      min: Math.min(domain.min, firstTick),
      max: Math.max(domain.max, lastTick),
    });

    const yScale = (v: number) =>
      finalDomain.max === finalDomain.min
        ? (plot.y0 + plot.y1) / 2
        : plot.y1 - ((v - finalDomain.min) / (finalDomain.max - finalDomain.min)) * (plot.y1 - plot.y0);

    return { xScale, yScale, ticks: tickValues, minT, maxT };
  }, [sorted, precision, healthyRange, gradualRange, recentRateKgPerWeek, plot]);

  const zeroY = yScale(0);

  /** Only what was drawn — see `ChartLegend`. */
  const legendItems: ChartLegendItem[] = [];
  legendItems.push({ label: 'Your week', className: styles.bar, shape: 'bar' });
  if (points.some((p) => p.requiredRateKg !== null)) {
    legendItems.push({ label: 'Required pace', className: styles.requiredLine });
  }
  if (healthyRange) {
    legendItems.push({ label: 'Healthy pace', className: styles.healthyBand, shape: 'band' });
  }
  if (gradualRange) {
    legendItems.push({ label: 'Gradual range', className: styles.gradualBoundary });
  }
  if (recentRateKgPerWeek !== null) {
    legendItems.push({ label: 'Your recent pace', className: styles.recentRateLine });
  }
  const pxPerMs = plot.x1 > plot.x0 && maxT > minT ? (plot.x1 - plot.x0) / (maxT - minT) : 0;
  const pixelsPerWeek = pxPerMs * WEEK_MS;

  const plotted = useMemo<Plotted[]>(
    () =>
      sorted.map((point) => {
        const centerX = xScale(weekTime(point));
        const { left, width: barWidth } = changeBarSpan(centerX, pixelsPerWeek, BAR_MAX_WIDTH, BAR_GAP);
        const y = point.actualChangeKg === null ? zeroY : yScale(point.actualChangeKg);
        const bar = planBarGeometry(point.actualChangeKg, y, zeroY, MIN_BAR_HEIGHT);
        return { point, x: centerX, left, width: barWidth, bar };
      }),
    [sorted, xScale, pixelsPerWeek, yScale, zeroY],
  );

  const requiredRuns = useMemo(() => {
    const ratePoints: RatePoint[] = sorted.map((point) => ({
      x: xScale(weekTime(point)),
      y: point.requiredRateKg === null ? null : yScale(point.requiredRateKg),
    }));
    return requiredRatePolylines(ratePoints);
  }, [sorted, xScale, yScale]);

  const spanMs = maxT - minT;
  const activePoint = activeIndex !== null ? plotted[activeIndex] : undefined;
  const last = plotted[plotted.length - 1];

  function nearestIndexForClientX(clientX: number, svgEl: SVGSVGElement | null): number | null {
    if (!svgEl || plotted.length === 0) return null;
    const rect = svgEl.getBoundingClientRect();
    if (rect.width === 0) return null;
    const viewX = ((clientX - rect.left) / rect.width) * width;
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < plotted.length; i++) {
      const candidate = plotted[i];
      if (!candidate) continue;
      const distance = Math.abs(candidate.x - viewX);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  function handlePointerMove(event: PointerEvent<SVGRectElement>) {
    const index = nearestIndexForClientX(event.clientX, event.currentTarget.ownerSVGElement);
    if (index !== null) setActiveIndex(index);
  }

  function handlePointerLeave() {
    if (!focusedRef.current) setActiveIndex(null);
  }

  function handleFocus() {
    focusedRef.current = true;
    setActiveIndex((current) => current ?? plotted.length - 1);
  }

  function handleBlur() {
    focusedRef.current = false;
    setActiveIndex(null);
  }

  function handleKeyDown(event: KeyboardEvent<SVGRectElement>) {
    if (plotted.length === 0) return;
    const current = activeIndex ?? plotted.length - 1;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex(Math.min(plotted.length - 1, current + 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex(Math.max(0, current - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(plotted.length - 1);
    }
  }

  function weekReadout(p: Plotted): string {
    const when = axisTickFormatter.format(new Date(p.point.weekStart));
    return `${when} — ${formatActualText(p.point.actualChangeKg, unit, precision)}, ${formatRequiredText(p.point.requiredRateKg, unit, precision)}`;
  }

  if (sorted.length === 0) {
    return (
      <div ref={frameRef} className={[styles.frame, className].filter(Boolean).join(' ')}>
        <div className={styles.empty}>
          <Text color="muted" size="sm">No plan data yet.</Text>
        </div>
      </div>
    );
  }

  const rootClassName = [styles.frame, className].filter(Boolean).join(' ');

  return (
    <div ref={frameRef} className={rootClassName}>
      <svg
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={
          last
            ? `Weekly weight plan: as of the week of ${axisTickFormatter.format(new Date(last.point.weekStart))}, ${formatActualText(last.point.actualChangeKg, unit, precision)}, against ${formatRequiredText(last.point.requiredRateKg, unit, precision)}. Use the chart's slider control to read every week.`
            : 'No plan data yet.'
        }
      >
        {/* Gridlines: solid hairlines, one shade off the surface — the zero
            baseline below is drawn separately, bolder, because it is the one
            line this chart is read against. */}
        <g aria-hidden="true">
          {ticks
            .filter((tick) => Math.abs(tick) > 1e-9)
            .map((tick) => {
              const y = yScale(tick);
              return (
                <g key={tick}>
                  <line className={styles.gridline} x1={plot.x0} x2={plot.x1} y1={y} y2={y} />
                  <text className={styles.tickLabel} x={plot.x0 - 8} y={y + 4} textAnchor="end">
                    {formatValue(tick, precision)}
                  </text>
                </g>
              );
            })}
        </g>

        {/* x-axis labels: a handful of evenly spaced points in time, the same
            idiom `MeasurementChart` uses for its own axis. */}
        <g aria-hidden="true">
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const t = minT + spanMs * fraction;
            const x = plot.x0 + fraction * (plot.x1 - plot.x0);
            return (
              <text
                key={fraction}
                className={styles.xTickLabel}
                x={x}
                y={height - 6}
                textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
              >
                {axisTickFormatter.format(new Date(t))}
              </text>
            );
          })}
        </g>

        {/* The healthy band: what a moderate deficit would actually produce
            for this person. A solid, low-opacity fill — the one band this
            chart invents nothing for: absent or null, nothing is drawn and no
            room is reserved. */}
        {healthyRange && (
          <rect
            aria-hidden="true"
            className={styles.healthyBand}
            x={plot.x0}
            y={Math.min(yScale(healthyRange.max), yScale(healthyRange.min))}
            width={Math.max(0, plot.x1 - plot.x0)}
            height={Math.abs(yScale(healthyRange.min) - yScale(healthyRange.max))}
          />
        )}

        {/* The gradual reference range: the population figure, profile-free.
            Drawn as a dashed outline rather than a fill so it reads as a
            looser reference than the healthy band above when both are
            present, never competing with it for the same visual weight. */}
        {gradualRange && (
          <g aria-hidden="true">
            <line
              className={styles.gradualBoundary}
              x1={plot.x0}
              x2={plot.x1}
              y1={yScale(gradualRange.max)}
              y2={yScale(gradualRange.max)}
            />
            <line
              className={styles.gradualBoundary}
              x1={plot.x0}
              x2={plot.x1}
              y1={yScale(gradualRange.min)}
              y2={yScale(gradualRange.min)}
            />
          </g>
        )}

        {/* The weekly bars: one accent colour whatever the sign, exactly the
            change strip's own rule — which side of the zero baseline a bar
            grows from is what says "towards" or "away from" the target, so
            the sign is never carried by hue. A week with no bar (`bar ===
            null`) draws a small hollow, dashed mark instead: never a filled
            shape, so it cannot be mistaken for a genuine zero-change week's
            solid sliver.

            A week still AHEAD of now gets nothing at all — not even that
            mark. It is not an unknown week, it is a week that has not
            happened, and marking the run-up to a deadline as a row of
            missing weigh-ins would read as failure that has not occurred.
            The required-rate line still runs through it, which is the whole
            reason those weeks are drawn. */}
        <g aria-hidden="true">
          {plotted.map((p) => {
            if (p.point.inFuture) return null;
            if (p.bar) {
              const d = planBarPath(p.left, p.bar.top, p.width, p.bar.height, p.bar.roundTop, BAR_RADIUS);
              return d ? (
                <path
                  key={p.point.weekStart}
                  d={d}
                  className={styles.bar}
                  data-active={activePoint === p ? 'true' : undefined}
                />
              ) : null;
            }
            return (
              <circle
                key={p.point.weekStart}
                className={styles.unknownMark}
                data-active={activePoint === p ? 'true' : undefined}
                cx={p.x}
                cy={zeroY}
                r={UNKNOWN_MARK_RADIUS}
              />
            );
          })}
        </g>

        {/* The zero baseline: always drawn, and bolder than a gridline,
            because the sign is the whole message. */}
        <line className={styles.zeroLine} x1={plot.x0} x2={plot.x1} y1={zeroY} y2={zeroY} />

        {/* The required-rate line: the single most important thing on this
            chart, because it MOVES — easing off when the reader is ahead of
            it, steepening when behind. Drawn from the per-week values
            directly, broken into separate runs across any week with no
            required rate, and never bridged across a gap. */}
        <g aria-hidden="true">
          {requiredRuns.map((run, i) =>
            run.length >= 2 ? (
              /* `pathLength` is what makes the draw-on reveal a reveal: the
                 stylesheet's `stroke-dasharray: 1` means one dash covering
                 the WHOLE line, and without this it means one user unit —
                 which rendered the line as a 1px dotted hairline and left it
                 that way once the animation finished. */
              <path key={i} className={styles.requiredLine} d={linearPath(run)} pathLength={1} />
            ) : (
              run[0] && <circle key={i} className={styles.requiredDot} cx={run[0].x} cy={run[0].y} r={3} />
            ),
          )}
        </g>

        {/* The reader's own recent pace: a dashed reference, never the
            required line's own colour, so the two are never confused. */}
        {recentRateKgPerWeek !== undefined && recentRateKgPerWeek !== null && (
          <g aria-hidden="true">
            <line
              className={styles.recentRateLine}
              x1={plot.x0}
              x2={plot.x1}
              y1={yScale(recentRateKgPerWeek)}
              y2={yScale(recentRateKgPerWeek)}
            />
            <text className={styles.recentRateLabel} x={plot.x1 - 4} y={yScale(recentRateKgPerWeek) - 6} textAnchor="end">
              Recent pace {formatSigned(recentRateKgPerWeek, unit, precision)}/wk
            </text>
          </g>
        )}

        {activePoint && (
          <line
            aria-hidden="true"
            className={styles.crosshairLine}
            x1={activePoint.x}
            x2={activePoint.x}
            y1={plot.y0}
            y2={plot.y1}
          />
        )}

        {/* The hit layer: a generous nearest-week target and the one
            focusable control that exposes every value — the same idiom
            `MeasurementChart`'s own reading slider uses, rather than a second
            pattern invented here. Hover enhances; it never gates. */}
        <rect
          x={plot.x0}
          y={plot.y0}
          width={Math.max(0, plot.x1 - plot.x0)}
          height={Math.max(0, plot.y1 - plot.y0)}
          className={styles.hitLayer}
          tabIndex={0}
          role="slider"
          aria-label={`Weekly weight plan — use the left and right arrow keys to move between weeks`}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, plotted.length - 1)}
          aria-valuenow={activeIndex ?? plotted.length - 1}
          aria-valuetext={activePoint ? weekReadout(activePoint) : last ? weekReadout(last) : undefined}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </svg>

      <ChartLegend items={legendItems} />


      {activePoint && (
        <div
          className={styles.tooltip}
          style={{
            left: `${(activePoint.x / width) * 100}%`,
            top: `${(activePoint.bar ? activePoint.bar.top : zeroY) / height * 100}%`,
          }}
          data-align={activePoint.x > width * 0.75 ? 'end' : activePoint.x < width * 0.25 ? 'start' : 'center'}
        >
          <Text as="div" size="2xs" color="muted" className={styles.tooltipTime}>
            {axisTickFormatter.format(new Date(activePoint.point.weekStart))}
          </Text>
          <Text as="div" family="figure" size="md" className={styles.tooltipValue}>
            {formatActualText(activePoint.point.actualChangeKg, unit, precision)}
          </Text>
          <Text as="div" size="2xs" color="muted">
            {formatRequiredText(activePoint.point.requiredRateKg, unit, precision)}
          </Text>
        </div>
      )}
    </div>
  );
}
